/** Centralized user-facing copy for admin manage pages. */
export const ADMIN_TEXTS = {
  common: {
    cancel: "Cancel",
    delete: "Delete",
    save: "Save",
    saveDraft: "Save Draft",
    publishChallenge: "Publish",
    saving: "Saving…",
    edit: "Edit",
    activate: "Activate",
    deactivate: "Deactivate",
    pending: "Pending",
    active: "Active",
    deactivated: "Deactivated",
    admin: "Admin",
    superAdmin: "Super Admin",
    platformEngineer: "Platform Engineer",
    teamLead: "Team Lead",
    relationshipManager: "Relationship Manager",
    streamer: "Streamer",
    close: "Close",
    view: "View",
    reset: "Reset",
    noResults: "No results",
    sortColumn: "Sort column",
    openMenu: "Open menu",
    localTimezone: "Local",
    emDash: "—",
    hoursSuffix: "hours",
    percentSuffix: "%",
    unknown: "Unknown",
    retry: "Try again",
    addPrize: "Add prize",
    duplicatePrize: "Duplicate prize",
    removePrize: "Remove prize",
    removePrizePendingOnly:
      "Only available (pending) prizes can be edited or removed",
  },

  sections: {
    personalDetails: "PERSONAL DETAILS",
    monthlyTargets: "MONTHLY TARGETS",
    streamingChannels: "STREAMING CHANNELS",
    accountSettings: "ACCOUNT SETTINGS",
    riskProfile: "RISK PROFILE",
    contract: "CONTRACT",
    importantDates: "IMPORTANT DATES (UTC)",
    challengeDetails: "CHALLENGE DETAILS",
    challengeRequirements: "REQUIREMENTS",
    challengeSchedule: "SCHEDULE (UTC)",
    challengeContent: "CONTENT",
    accountNotes: "ACCOUNT NOTES",
  },

  fields: {
    name: "Name",
    email: "Email",
    emailRequired: "Email *",
    username: "Username",
    usernameRequired: "Username *",
    address: "Address",
    language: "Language",
    countryOfResidence: "Country of Residence",
    region: "Region",
    timezone: "Timezone",
    relationshipManager: "Relationship Manager",
    discord: "Discord",
    airtime: "Airtime",
    shareOfAirtime: "Share of Airtime",
    password: "Password",
    marketingConsent: "Marketing Consent",
    contractUrl: "Contract URL",
    channelUrl: "Channel URL",
    contractExpiry: "Contract Expiry",
    createdAt: "Created",
    policyAcceptedAt: "Policy Accepted",
    lastLoginAt: "Last Login",
    accountType: "Account Type",
    location: "Location",
    reporting: "Reporting To",
    title: "Title",
    objective: "Objective",
    minimumBetRequirement: "Minimum Bet Requirement",
    minimumViewerRequirement: "Minimum Viewer Requirement",
    restrictedFeatures: "Restricted Features",
    rules: "Rules",
    startDateTime: "Start date & time (UTC)",
    endDateTime: "End date & time (UTC)",
    prizes: "Prizes",
    prizesWithCount: (count: number) => `Prizes (${count})`,
    prizeOrder: (order: number) => `${order}`,
    prizeOrderLabel: (order: number) => `Prize ${order}`,
    prizeTitle: "Prize title",
    prizeValue: "Prize value",
    coverImage: "Cover image",
    existingCoverImage: (fileName: string) =>
      `Current cover image: ${fileName}`,
    riskProfile: "Risk Profile",
    trackingStatus: "Tracking Status",
    preferredGameType: "Preferred Game Type",
  },

  placeholders: {
    username: "Username",
    accountType: "Account type",
    location: "Enter a location",
    reporting: "Select a reporting manager",
    riskProfile: "Select a risk level",
    trackingStatus: "Select a tracking status",
    preferredGameType: "Select a preferred game type",
    language: "Select a language",
    countryOfResidence: "Select a country",
    region: "Select a region",
    timezone: "Select a timezone",
    relationshipManager: "Select a relationship manager",
    contractUrl: "https://www.example.com",
    channelUrl: "https://www.example.com/channel",
    pickDate: "Pick a date",
    addNote: "Add a note about this account...",
    searchStreamers: "Search streamers...",
    searchAdmins: "Search admins...",
    challengeTitle: "Enter challenge title",
    challengeObjective: "Enter challenge objective",
    restrictedFeatures: "Describe restricted features",
    selectCoverImage: "Select a cover image",
    prizeTitle: "1st Prize",
    prizeValue: "1000",
  },

  aria: {
    accountType: "Account type",
    location: "Location",
    reporting: "Reporting manager",
    riskProfile: "Risk profile",
    openChannelUrl: "Open channel URL in a new tab",
    openContractUrl: "Open contract URL in a new tab",
    addChannelUrl: "Add channel URL",
    removeChannelUrl: "Remove channel URL",
    toggleChannelActive: "Enable/disable tracking for this channel",
    addAccountNote: "Add account note",
    sortLastStreamed: "Sort Last Streamed",
  },

  validation: {
    nameRequired: "Name is required",
    emailRequired: "Email is required",
    emailInvalid: "Please enter a valid email address",
    accountTypeRequired: "Account type is required",
    usernameRequired: "Username is required",
    addressMaxLength: "Address must be 500 characters or fewer",
    platformsMin: "Add at least one streaming platform",
    platformsIncomplete: "Add at least one channel URL",
    naturalNumber: "Must be a natural number",
    shareRange: "Must be 0–100",
    validDate: "Must be a valid date",
    contractUrlHttp: "URL must start with http:// or https://",
    contractUrlComplete: "Enter a complete URL (e.g. https://www.example.com)",
    contractUrlProtocol: "URL must use http:// or https://",
    contractUrlDomain:
      "URL must have a valid domain extension (e.g. .com, .org, .net)",
  },

  toasts: {
    streamerAdded: "Streamer Added Successfully",
    streamerUpdated: "Streamer updated successfully",
    streamerMissingId: "Cannot update: missing streamer id",
    streamerAddFailed: "Failed to add streamer",
    streamerUpdateFailed: "Failed to update streamer",
    profileUpdated: "Profile updated successfully",
    profileUpdateFailed: "Failed to update profile",
    unknownError: "Unknown error",
    adminCreated: "Admin created successfully",
    adminUpdated: "Admin updated successfully",
    adminMissingId: "Cannot update: missing user id",
    adminCreateFailed: "Failed to create admin",
    adminUpdateFailed: "Failed to update admin",
    challengeDraftSaved: "Challenge saved as draft",
    challengePublished: "Challenge published successfully",
    challengeUpdated: "Challenge updated successfully",
    challengeCreateFailed: "Failed to create challenge",
    challengeUpdateFailed: "Failed to update challenge",
    challengePublishFailed: "Failed to publish challenge",
    challengeOfflineSuccess: "Challenge taken offline",
    challengeOfflineFailed: "Failed to take challenge offline",
    clipAccepted: "Clip accepted. Pending prize approval.",
    clipAcceptFailed: "Failed to accept clip submission",
    clipAwarded: "Clip awarded",
    clipAwardFailed: "Failed to award clip submission",
    clipRejected: "Clip submission rejected",
    clipRejectFailed: "Failed to reject clip submission",
    clipAcceptanceCancelled: "Clip acceptance cancelled",
    clipAcceptanceCancelFailed: "Failed to cancel clip acceptance",
    prizeUnavailable:
      "That prize is no longer available. Accept the clip against a different prize.",
    noteAdded: "Note added",
    noteAddFailed: "Failed to add note",
    activationResent: "Activation email resent successfully",
    activationResendFailed: "Failed to resend activation email",
    activationMissingId: "Failed to resend activation email: missing user ID",
    passwordResetSent: "Password reset email sent successfully",
    passwordResetFailed: "Failed to send password reset email",
    passwordResetMissingId:
      "Failed to send password reset email: missing user ID",
    streamStatsSyncRetried: "Stream stats sync retry started",
    streamStatsSyncRetryFailed: "Failed to retry stream stats sync",
    streamStatsSyncRetryMissingId:
      "Failed to retry stream stats sync: missing user ID",
    streamStatsBackfillCompleted: "Stream stats backfill completed",
    streamStatsBackfillFailed: "Failed to run stream stats backfill",
    statusUpdated: (label: string, deactivated: boolean) =>
      deactivated
        ? `${label} deactivated successfully`
        : `${label} activated successfully`,
    statusUpdateFailed: (
      label: string,
      deactivate: boolean,
      detail?: string
    ) => {
      const base = deactivate
        ? `Failed to deactivate ${label}`
        : `Failed to activate ${label}`
      const reason = detail?.trim()
      if (!reason) return base
      return `${base}. ${reason}`
    },
    statusMissingId: (labelLower: string) =>
      `Failed to update ${labelLower} status: missing user ID`,
  },

  columns: {
    username: "Username",
    name: "Name",
    email: "Email",
    channels: "Channels",
    language: "Language",
    segment: "Segment",
    segmentNew: "New",
    accountStatus: "Acc. Status",
    streamStatsSyncStatus: "Sync Status",
    relationshipManager: "Rel. Manager",
    airtime: "Airtime",
    shareOfAirtime: "Share of Airtime",
    lastStreamed: (tz: string) => `Last Streamed (${tz})`,
    accountType: "Account Type",
    createdAt: (tz: string) => `Created At (${tz})`,
  },

  actions: {
    viewStreamStats: "View Stream Stats",
    sendPasswordResetEmail: "Send Password Reset Email",
    resendActivationEmail: "Resend Activation Email",
    retryStreamStatsSync: "Retry",
    streamStatsBackfill: "Backfill",
  },

  deleteConfirm: {
    body: (entityName: string) =>
      `Are you sure you want to delete ${entityName}? This action cannot be undone.`,
  },

  list: {
    allStreamers: "All Streamers",
    allAdmins: "All Admins",
    manageChallenges: "Manage Challenges",
    challengeDescription:
      "Create and manage challenges available in StreamHub.",
    challengesLoading: "Loading challenges",
    challengesLoadFailed: "Challenges could not be loaded.",
    noChallengesInStatus: "No challenges in this category.",
    challengeNotFound: "Challenge not found",
    challengeNotFoundDescription:
      "Select a challenge from Manage Challenges and try again.",
    challengeLoadFailed: "This challenge could not be loaded.",
    back: "Back",
    backToChallenges: "Back to Manage Challenges",
    pendingClipSubmissions: "Pending clip submissions",
    pendingClipDescription:
      "Review streamer clip URLs in chronological order (oldest first). Accept awards the next available prize after the MP4 upload succeeds.",
    acceptedClipSubmissions: "Accepted clip submissions",
    rejectedClipSubmissions: "Rejected clip submissions",
    noPendingClips: "No pending clip submissions.",
    noAcceptedClips: "No accepted clip submissions.",
    noRejectedClips: "No rejected clip submissions.",
    takeOffline: "Unpublish",
    takeOfflineTitle: "Take challenge offline",
    takeOfflineBody: (title: string) =>
      `Take “${title}” offline? It will be removed from the StreamHub front end.`,
    editChallenge: "Edit Challenge",
    challengeInformation: "Challenge information",
    noValueProvided: "Not provided",
    statStart: "Start (UTC)",
    statEnd: "End (UTC)",
    statPending: "Pending Reviews",
    statWinners: "Winners",
    requirements: "Requirements",
    prizeAwarded: "Awarded",
    prizePending: "Available",
    prizeInReview: "In Review",
    prizeWinner: (name: string) => `Winner: ${name}`,
  },

  clipReview: {
    clipSubmissions: "Clip submissions",
    clipSubmissionsDescription:
      "Review streamer clip URLs in chronological order, oldest first. Accept awards a prize after the MP4 upload succeeds.",
    filterPending: (count: number) => `Pending (${count})`,
    filterAwarded: (count: number) => `Awarded (${count})`,
    filterAcceptance: (count: number) => `Accepted (${count})`,
    filterRejected: (count: number) => `Rejected (${count})`,
    noAwardedClips: "No awarded clip submissions.",
    noAcceptanceClips: "No acceptance clip submissions.",
    pending: "Pending",
    autoRejected: "Auto-rejected",
    streamerUsername: "Streamer username",
    clipUrl: "Clip URL",
    prizeWon: "Prize",
    submittedAt: "Submitted",
    acceptedAt: "Accepted",
    acceptedBy: "Accepted by",
    rejectedAt: "Rejected",
    rejectedBy: "Rejected by",
    awardedAt: "Awarded",
    awardedBy: "Awarded by",
    rejectionReason: "Review Note (Internal)",
    streamerExplanation: "Streamer Explanation",
    clipVideo: "Clip video",
    playClip: "Play clip",
    reviewNote: "Review Note (Internal)",
    accept: "Accept",
    award: "Award",
    reject: "Reject",
    cancelAcceptance: "Reject",
    acceptTitle: "Accept clip submission",
    awardTitle: "Award prize",
    awardBody: (prizeLabel: string, streamerName: string) =>
      `Are you sure you want to award ${prizeLabel} to ${streamerName}?`,
    confirmAward: "Confirm award",
    rejectTitle: "Reject clip submission",
    cancelAcceptanceTitle: "Cancel clip acceptance",
    cancelAcceptanceBody: (username: string) =>
      `Cancel the acceptance for ${username}?`,
    confirmCancelAcceptance: "Reject",
    lastPrizeWarning:
      "This is the last available prize. Accepting it will auto-reject any other pending clips and email those streamers.",
    reviewUnavailable:
      "Review actions are unavailable while this challenge is draft or upcoming.",
    finishAccept: "Finish",
    finishAcceptUploading: "Uploading…",
    confirmReject: "Reject clip",
    selectPrize: "Select Prize",
    selectVideo: "Select MP4 clip file",
    videoHint:
      "MP4 only, up to 500MB. Acceptance completes only after upload succeeds.",
    clipTitle: "Clip title",
    clipTitlePlaceholder: "Enter a title for this clip",
    reviewNotePlaceholder: "Enter a note for the streamer",
    rejectReasonPlaceholder: "Enter an internal review note",
    streamerExplanationPlaceholder: "Enter the reason for rejecting this clip",
    openClipUrl: "Open clip URL",
    openClipVideo: "Open uploaded clip",
    acceptance: "Accepted",
    awarded: "Awarded",
    rejected: "Rejected",
    noAvailablePrizes: "No available prizes remain for this challenge.",
    acceptDisabledNoPrizes:
      "Accept is unavailable until a prize is free again.",
    submissionInProgress: "A submission is in progress. Please wait.",
    submissionsLoading: "Loading clip submissions",
    submissionsLoadingMore: "Loading more clip submissions",
    submissionsLoadFailed: "Clip submissions could not be loaded.",
    validation: {
      titleRequired: "Enter a clip title",
      prizeRequired: "Select a prize",
      reviewNoteRequired: "Enter a review note",
      streamerExplanationRequired: "Enter a streamer explanation",
      videoRequired: "Choose an MP4 clip file",
      videoFormat: "Only MP4 video files are supported",
      videoTooLarge: "Clip file must be 500MB or smaller",
    },
  },

  challengeForm: {
    prizesEditHint:
      "Only available prizes on live challenges can be edited or removed.",
    prizeValueHint: "(Optimal - up to 10 characters)",
    characterCount: (count: number, max: number) => `${count} / ${max}`,
    validation: {
      required: "This field is required",
      numberMin: "Enter a number greater than or equal to zero",
      dateTimeFormat: "Enter a valid UTC date and time",
      coverImageRequired: "Choose a valid cover image",
      endDateAfterStart: "End date and time must be after the start",
      prizesRequired: "This field is required",
      prizeTitleMaxLength: "Prize title must be 40 characters or fewer",
      prizeValueMaxLength: "Prize value must be 40 characters or fewer",
      objectiveMaxLength: "Objective must be 128 characters or fewer",
      restrictedFeaturesMaxLength:
        "Restricted features must be 96 characters or fewer",
    },
  },

  challengeStatus: {
    LIVE: "Live",
    DRAFT: "Draft",
    UPCOMING: "Upcoming",
    ENDED: "Ended",
  },

  challengeCard: {
    coverImageAlt: (title: string) => `Cover image for ${title}`,
    viewChallenge: (title: string) => `View challenge: ${title}`,
    editChallenge: (title: string) => `Edit challenge: ${title}`,
    startDate: (date: string) => `Start(UTC): ${date}`,
    endDate: (date: string) => `End(UTC): ${date}`,
    pendingClips: (count: number) =>
      `${count} ${count === 1 ? "Clip" : "Clips"} Pending`,
  },

  drawers: {
    addStreamer: "Add Streamer",
    addStreamerDescription: "Add a new streamer to the platform.",
    editStreamer: "Edit Streamer",
    editStreamerDescription: "Update streamer details.",
    addAdmin: "Add Admin",
    addAdminDescription: "Add a new admin to the platform.",
    editAdmin: "Edit Admin",
    editAdminDescription: "Update admin details.",
    createChallenge: "Create New Challenge",
    createChallengeDescription:
      "Save a title-only draft or complete every field to publish.",
    editChallenge: "Edit Challenge",
    editChallengeDescription:
      "Update challenge details. Save as draft or publish changes.",
  },

  notes: {
    unknownDate: "Unknown date",
    composeTitle: "Add a note",
    addNote: "Add Note",
    empty: "No notes yet. Add one above.",
    byAuthor: (name: string) => `by ${name}`,
  },

  empty: {
    noChannel: "No channel found for this user",
  },

  streamStatsSync: {
    NOT_SYNCED: "Not synced",
    SYNC_PENDING: "Pending",
    SYNCED: "Synced",
    SYNC_FAILED: "Failed",
  },

  channelSync: {
    PENDING: {
      badge: "Sync pending",
      title: "This channel is being synced.",
      description:
        "No action is needed. Please wait; the status updates automatically once the sync completes.",
    },
    INVALID: {
      badge: "Sync failed",
      title: "This channel failed to sync.",
      description:
        "No streaming data is being collected for it. Action required: update the channel details to start syncing.",
    },
    ALREADY_EXISTS: {
      title: "Duplicate:",
      description: "This channel already exists.",
    },
    NOT_VALID: {
      title: "Invalid:",
      description: "Invalid channel URL.",
    },
  },

  accountKind: {
    internal: "Admin",
    streamer: "Streamer",
  },

  riskLevels: {
    GREEN: {
      text: "Green",
      description: "Open sponsorship",
    },
    AMBER: {
      text: "Amber",
      description: "Sponsor but with care",
    },
    RED: {
      text: "Red",
      description: "Distanced sponsorship",
    },
    BLACK: {
      text: "Black",
      description: "No sponsorship",
    },
  },
} as const
